import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import type { SharedFlight, SharedHotel, SharedBooking } from '../../types';
import { fmtUTCDateTime, fmtUTCTime } from '../../utils/dateTime';
import { Icon, Plane, Hotel as HotelIcon, Ticket } from '../../components/ui/Icon';
import { SharedNote, EmptyNote } from './shared.ui';

/**
 * Flights, stays and bookings as a traveller needs to read them.
 *
 * What is NOT here is the point: no `bookingRef` and no attached documents. The
 * server never sends either (`sharedTrip.service.ts`), because a locator is a
 * bearer credential and a document filename is a public-bucket object key.
 */

interface Props {
  flights: SharedFlight[];
  hotels: SharedHotel[];
  bookings: SharedBooking[];
}

function Row({ icon, tone, title, subtitle, notes }: {
  icon: React.ElementType; tone: string; title: string; subtitle: string; notes?: string | null;
}) {
  return (
    <div className="card px-4 py-3 flex items-start gap-3">
      <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${tone}`}>
        <Icon as={icon} size={16} />
      </div>
      <div className="min-w-0">
        <p className="text-sm font-medium text-slate-900 dark:text-slate-100">{title}</p>
        <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{subtitle}</p>
        <SharedNote text={notes} />
      </div>
    </div>
  );
}

export default function SharedBookings({ flights, hotels, bookings }: Props) {
  const { t } = useTranslation();

  if (flights.length + hotels.length + bookings.length === 0) {
    return <EmptyNote>{t('shared.noBookings')}</EmptyNote>;
  }

  return (
    <div className="space-y-6">
      {flights.length > 0 && (
        <section className="space-y-2">
          <h3 className="font-mono text-[10px] uppercase tracking-widest text-slate-400">{t('shared.sections.flights')}</h3>
          {flights.map(f => (
            <Row
              key={f.id}
              icon={Plane}
              tone="bg-sky-50 dark:bg-sky-900/30 text-sky-500"
              title={`${f.direction === 'OUTBOUND' ? t('shared.outbound') : t('shared.return')}${f.airline ? ` · ${f.airline}` : ''}${f.flightNumber ? ` ${f.flightNumber}` : ''}`}
              subtitle={`${f.departureAirport} → ${f.arrivalAirport} · ${fmtUTCDateTime(f.departureTime, 'MMM d')} – ${fmtUTCTime(f.arrivalTime)}`}
              notes={f.notes}
            />
          ))}
        </section>
      )}

      {hotels.length > 0 && (
        <section className="space-y-2">
          <h3 className="font-mono text-[10px] uppercase tracking-widest text-slate-400">{t('shared.sections.accommodation')}</h3>
          {hotels.map(h => (
            <Row
              key={h.id}
              icon={HotelIcon}
              tone="bg-indigo-50 dark:bg-indigo-900/30 text-indigo-500"
              title={h.name}
              subtitle={`${format(new Date(h.checkIn), 'MMM d')} → ${format(new Date(h.checkOut), 'MMM d')}${h.address ? ` · ${h.address}` : ''}`}
              notes={h.notes}
            />
          ))}
        </section>
      )}

      {bookings.length > 0 && (
        <section className="space-y-2">
          <h3 className="font-mono text-[10px] uppercase tracking-widest text-slate-400">{t('shared.sections.bookings')}</h3>
          {bookings.map(b => (
            <Row
              key={b.id}
              icon={Ticket}
              tone="bg-teal-50 dark:bg-teal-900/30 text-teal-500"
              title={b.title}
              subtitle={[
                format(new Date(b.date), 'MMM d'),
                b.startTime,
                b.venue,
                b.address,
              ].filter(Boolean).join(' · ')}
              notes={b.notes}
            />
          ))}
        </section>
      )}
    </div>
  );
}
